<template>
  <div class="max-w-4xl w-full mx-auto space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">Snapshot Bulk Upload</h1>
    <div v-if="selected_university" class="mt-1">
      <a :href="snapshotPath" target="_blank" rel="noopener" class="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50">
        Open snapshot page ({{ selected_university.slug }})
      </a>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md border"
        :class="ui.mode === 'bulk' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50 border-gray-300'"
        @click="setMode('bulk')"
      >
        Bulk upload
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-sm rounded-md border"
        :class="ui.mode === 'camera' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white hover:bg-gray-50 border-gray-300'"
        @click="setMode('camera')"
      >
        Shoot on iPhone
      </button>
      <span v-if="isCameraMode" class="text-xs text-gray-500">Camera mode auto-uploads after each shot.</span>
    </div>

    <!-- University selector -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Select University</label>
      <select v-model="selected_university" @change="onUniChange" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select University</option>
        <option v-for="u in universities" :key="u.id" :value="u">{{ u.name }}</option>
      </select>
    </div>

    <!-- Ceremony selector -->
    <div v-if="selected_university">
      <label class="block text-sm font-medium text-gray-700 mb-1">Select Ceremony</label>
      <select v-model="selected_ceremony" class="w-full h-10 px-3 border rounded-lg">
        <option disabled :value="null">Select ceremony</option>
        <option v-for="c in ceremonies" :key="c.id" :value="c.id">
          {{ formatCeremonyOption(c) }}
        </option>
      </select>
    </div>

    <div v-if="selected_ceremony && isCameraMode" class="camera-stage">
      <div class="camera-topbar">
        <div class="camera-topbar-left">
          <div class="camera-title">Snapshot Camera</div>
          <div class="camera-subtitle">Shoot continuously and upload into this ceremony.</div>
        </div>
        <div class="camera-topbar-right">
          <button type="button" class="camera-text-button" @click="setMode('bulk', { syncRoute: false })">Exit camera</button>
        </div>
      </div>

      <div class="camera-preview">
        <video ref="cameraVideo" class="camera-video" autoplay playsinline muted></video>
        <div v-if="!camera.active" class="camera-placeholder">
          <div class="camera-placeholder-title">Camera ready</div>
          <div class="camera-placeholder-text">Tap start to grant camera access.</div>
        </div>
      </div>

      <div class="camera-controls">
        <div class="camera-controls-left">
          <button
            type="button"
            class="camera-button"
            :class="camera.active ? 'camera-button-secondary' : 'camera-button-primary'"
            @click="camera.active ? stopCamera() : startCamera()"
          >
            {{ camera.active ? 'Stop' : (camera.starting ? 'Starting…' : 'Start camera') }}
          </button>
          <button
            type="button"
            class="camera-button camera-button-secondary"
            :disabled="!camera.active"
            @click="capturePhoto"
          >
            Shutter
          </button>
          <button
            type="button"
            class="camera-button camera-button-secondary"
            :disabled="!camera.active"
            @click="switchCamera"
          >
            Flip
          </button>
          <label class="camera-button camera-button-secondary">
            <span>Photo picker</span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              class="hidden"
              :disabled="!selected_ceremony"
              ref="cameraInput"
              @change="onCameraCapture"
            />
          </label>
        </div>
        <div class="camera-controls-right">
          <label class="camera-select-label" v-if="camera.devices.length">
            <span>Camera</span>
            <select
              class="camera-select"
              v-model="camera.selectedDeviceId"
              @change="onCameraDeviceChange"
            >
              <option v-for="device in camera.devices" :key="device.deviceId" :value="device.deviceId">
                {{ device.label || `Camera ${device.index}` }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <div v-if="camera.error" class="camera-error">{{ camera.error }}</div>
    </div>

    <!-- Dropzone -->
    <div v-if="selected_ceremony && !isCameraMode">
      <div v-if="!ui.dropzoneCollapsed"
        class="relative border-2 border-dashed rounded-lg p-8 text-center bg-white"
        @dragover.prevent
        @dragenter.prevent
        @drop.prevent="onDrop"
      >
        <button type="button" @click="ui.dropzoneCollapsed = true" class="absolute top-2 right-2 text-xs text-gray-500 hover:text-gray-700">Collapse</button>
        <p class="text-gray-700">Drag and drop images or folders here</p>
        <div class="text-xs text-gray-500">Folders (with nested subfolders) are supported.</div>
        <div class="mt-3">
          <label class="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 cursor-pointer">
            <span>Select files or a folder…</span>
            <input type="file" multiple webkitdirectory directory accept="image/*" @change="onFileChange" class="hidden" />
          </label>
        </div>
        <div v-if="collecting" class="mt-3 text-sm text-gray-600">
          Processing files… Found {{ collectStats.found }}
        </div>
      </div>
      <div v-else class="bg-white border rounded-lg p-3 flex items-center justify-between text-sm">
        <div class="text-gray-700">Upload area collapsed.</div>
        <div class="space-x-2">
          <button type="button" @click="ui.dropzoneCollapsed = false" class="px-2 py-1 rounded border bg-white hover:bg-gray-50">Expand</button>
        </div>
      </div>
    </div>

    <!-- Files table / overall progress -->
    <div v-if="files.length">
      <div v-if="largeUploadNotice" class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        {{ largeUploadNotice }}
      </div>
      <div class="flex justify-between items-center">
        <div class="w-full">
          <div class="flex items-center justify-between">
            <h2 class="text-lg font-semibold">{{ files.length }} file(s) ready</h2>
            <div class="text-xs text-gray-600">{{ doneCount }} done</div>
          </div>
          <!-- Overall progress bar -->
          <div class="mt-2">
            <div class="w-full bg-gray-200 rounded-full h-3">
              <div class="bg-blue-600 h-3 rounded-full" :style="{ width: overallPercent + '%' }"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1 flex items-center justify-between">
              <div>
                {{ formatBytes(overall.uploadedBytes) }} / {{ formatBytes(overall.totalBytes) }} ({{ overallPercent.toFixed(0) }}%)
              </div>
              <div class="space-x-4">
                <span v-if="stats.skipped > 0">Skipped: {{ stats.skipped }}</span>
                <span v-if="stats.renamed > 0">Renamed: {{ stats.renamed }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="ml-4 whitespace-nowrap space-x-2">
          <button :disabled="uploading" @click="clearQueue" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Clear</button>
          <button :disabled="uploading" @click="requestUpload" class="px-4 py-2 rounded-md border bg-white hover:bg-gray-50">
            {{ uploading ? 'Uploading…' : 'Start Upload' }}
          </button>
          <button v-if="!ui.listCollapsed" @click="ui.listCollapsed = true" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Hide list</button>
          <button v-else @click="ui.listCollapsed = false" class="px-3 py-2 rounded-md border bg-white hover:bg-gray-50">Show list</button>
        </div>
      </div>

      <ul v-show="!ui.listCollapsed" class="divide-y bg-white rounded-md border mt-2 max-h-[60vh] overflow-auto">
        <li v-for="f in files" :key="f.id" class="p-3 flex items-center justify-between">
          <div class="truncate w-1/2 pr-2">
            <div class="font-medium truncate flex items-center space-x-2">
              <span class="truncate" :title="f.finalName">{{ f.finalName }}</span>
              <span v-if="f._renamed" class="text-[10px] rounded px-1 py-0.5 bg-yellow-100 text-yellow-700 border border-yellow-300">renamed</span>
            </div>
            <div class="text-xs text-gray-500 truncate" :title="f.relPath || f.file.name">From: {{ f.relPath || f.file.name }}</div>
            <div class="text-xs text-gray-400">{{ (f.file.size/1024/1024).toFixed(2) }} MB • {{ f.mime }}</div>
          </div>
          <div class="w-1/2 ml-4">
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div class="bg-blue-600 h-2 rounded-full" :style="{ width: (f.progress||0) + '%' }"></div>
            </div>
            <div class="text-xs text-gray-500 mt-1">{{ f.status }}</div>
          </div>
        </li>
      </ul>
    </div>

    <div v-if="terminalCount>0 && terminalCount===files.length" class="p-3 rounded bg-green-50 text-green-700">
      <div class="flex items-center justify-between">
        <span>All originals uploaded. Processing continues in the backend. Uploaded: {{ uploadedCount }} · Skipped: {{ skippedDuplicateCount }} · Failed: {{ failedCount }}</span>
        <div class="flex gap-3">
          <router-link :to="collectionAdminPath" class="underline font-medium">Review photo set</router-link>
          <a :href="snapshotPath" target="_blank" rel="noopener" class="underline font-medium">View snapshots page</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'

export default {
  name: 'SnapshotBulkUpload',
  data() {
    return {
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      uploadSessionId: '',
      files: [],
      stats: { skipped: 0, renamed: 0, uploaded: 0, skippedDuplicates: 0, failed: 0 },
      overall: { totalBytes: 0, uploadedBytes: 0 },
      ui: { dropzoneCollapsed: false, listCollapsed: false, mode: 'bulk' },
      collecting: false,
      collectStats: { found: 0 },
      uploading: false,
      concurrency: 8,
      presignBatchSize: 100,
      maxRetries: 3,
      uploadRequested: false,
      cameraLastOpenAt: 0,
      camera: {
        active: false,
        starting: false,
        error: null,
        stream: null,
        facingMode: 'environment',
        sequence: 0,
        devices: [],
        selectedDeviceId: '',
      },
    }
  },
  computed: {
    doneCount() {
      return this.files.filter(f => ['processing', 'done'].includes(f.status)).length
    },
    terminalCount() {
      return this.files.filter(f => ['processing', 'done', 'skipped duplicate', 'error'].includes(f.status)).length
    },
    uploadedCount() {
      return this.files.filter(f => ['processing', 'done'].includes(f.status)).length
    },
    skippedDuplicateCount() {
      return this.files.filter(f => f.status === 'skipped duplicate').length
    },
    failedCount() {
      return this.files.filter(f => f.status === 'error').length
    },
    snapshotPath() {
      return this.selected_university ? `/snapshot/${this.selected_university.slug}` : '#'
    },
    collectionAdminPath() {
      return this.selected_ceremony ? `/admin/snapshot-photo-sets/${this.selected_ceremony}/photos` : '/admin/snapshot-bulk'
    },
    overallPercent() {
      const total = this.overall.totalBytes || 0
      const uploaded = Math.min(this.overall.uploadedBytes || 0, total)
      return total ? Math.max(0, Math.min(100, (uploaded / total) * 100)) : 0
    },
    isCameraMode() {
      return this.ui.mode === 'camera'
    },
    largeUploadNotice() {
      if (this.files.length >= 10000) {
        return 'This is a very large upload. Keep this browser open until all originals are uploaded, then use Snapshot Operations to monitor backend processing.'
      }
      if (this.files.length >= 1000) {
        return 'Large upload queued. Uploading happens from this browser; Snapshot Operations tracks backend processing after originals reach S3.'
      }
      return ''
    },
  },
  created() {
    // Tune concurrency based on connection (best-effort)
    try {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
      if (conn && conn.effectiveType) {
        if (conn.effectiveType.includes('2g')) this.concurrency = 1
        else if (conn.effectiveType.includes('3g')) this.concurrency = 2
        else this.concurrency = 4
      }
    } catch (e) {
      // Connection hints are best-effort only.
    }
    axios.get(`/universities`).then(r => this.universities = r.data)
    this.syncModeFromRoute()
  },
  watch: {
    '$route.query.mode'() {
      this.syncModeFromRoute()
    },
    selected_ceremony(newVal) {
      if (newVal && this.isCameraMode) this.scheduleCameraStart()
    },
    isCameraMode(newVal) {
      if (newVal && this.selected_ceremony) this.scheduleCameraStart()
      if (!newVal) this.stopCamera()
    },
  },
  beforeDestroy() {
    this.stopCamera()
  },
  methods: {
    syncModeFromRoute() {
      const mode = this.$route?.query?.mode
      if (mode === 'camera' || mode === 'shoot' || mode === 'iphone') {
        this.ui.mode = 'camera'
      } else {
        this.ui.mode = 'bulk'
      }
    },
    setMode(mode, options = {}) {
      const nextMode = mode === 'camera' ? 'camera' : 'bulk'
      this.ui.mode = nextMode
      if (options.syncRoute !== false && this.$router && this.$route) {
        const query = { ...this.$route.query }
        if (nextMode === 'camera') query.mode = 'camera'
        else delete query.mode
        this.$router.replace({ query })
      }
      if (nextMode === 'camera' && this.selected_ceremony) {
        this.scheduleCameraStart()
      }
    },
    scheduleCameraStart() {
      this.$nextTick(() => {
        const now = Date.now()
        if (now - this.cameraLastOpenAt < 800) return
        this.cameraLastOpenAt = now
        this.startCamera(true)
      })
    },
    async startCamera(auto = false) {
      if (this.camera.active || this.camera.starting) return
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.camera.error = 'Camera not supported in this browser.'
        return
      }
      this.camera.starting = true
      this.camera.error = null
      try {
        const constraints = {
          video: this.camera.selectedDeviceId
            ? { deviceId: { exact: this.camera.selectedDeviceId }, width: { ideal: 4032 }, height: { ideal: 3024 } }
            : { facingMode: { ideal: this.camera.facingMode }, width: { ideal: 4032 }, height: { ideal: 3024 } },
          audio: false,
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        this.camera.stream = stream
        this.camera.active = true
        try {
          const track = stream.getVideoTracks()[0]
          if (track && track.getCapabilities && track.applyConstraints) {
            const caps = track.getCapabilities()
            const width = caps.width && caps.width.max ? caps.width.max : undefined
            const height = caps.height && caps.height.max ? caps.height.max : undefined
            if (width || height) {
              await track.applyConstraints({ width, height })
            }
          }
          const settings = track && track.getSettings ? track.getSettings() : null
          if (settings && settings.deviceId && !this.camera.selectedDeviceId) {
            this.camera.selectedDeviceId = settings.deviceId
          }
        } catch (e) { /* ignore */ }
        const video = this.$refs.cameraVideo
        if (video) {
          video.srcObject = stream
          try { await video.play() } catch (e) { /* ignore */ }
        }
        await this.refreshCameraDevices()
      } catch (err) {
        const name = err && err.name
        if (auto) {
          this.camera.error = 'Tap “Start camera” to allow access.'
        } else if (name === 'NotAllowedError' || name === 'SecurityError') {
          this.camera.error = 'Camera access denied. Please allow it and try again.'
        } else {
          this.camera.error = 'Unable to start camera.'
        }
      } finally {
        this.camera.starting = false
      }
    },
    stopCamera() {
      if (this.camera.stream) {
        this.camera.stream.getTracks().forEach(t => t.stop())
      }
      this.camera.stream = null
      this.camera.active = false
    },
    async refreshCameraDevices() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cameras = devices.filter(d => d.kind === 'videoinput')
      this.camera.devices = cameras.map((d, idx) => ({
        deviceId: d.deviceId,
        label: d.label || `Camera ${idx + 1}`,
        index: idx + 1,
      }))
      if (!this.camera.selectedDeviceId && this.camera.devices.length) {
        this.camera.selectedDeviceId = this.camera.devices[0].deviceId
      }
    },
    async onCameraDeviceChange() {
      if (!this.camera.selectedDeviceId) return
      if (this.camera.active) {
        this.stopCamera()
        await this.startCamera()
      }
    },
    async switchCamera() {
      if (this.camera.devices.length > 1) {
        const ids = this.camera.devices.map(d => d.deviceId)
        const currentIndex = ids.indexOf(this.camera.selectedDeviceId)
        const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % ids.length : 0
        this.camera.selectedDeviceId = ids[nextIndex]
        await this.onCameraDeviceChange()
        return
      }
      this.camera.facingMode = this.camera.facingMode === 'environment' ? 'user' : 'environment'
      if (this.camera.active) {
        this.stopCamera()
        await this.startCamera()
      }
    },
    async ensureVideoReady() {
      const video = this.$refs.cameraVideo
      if (!video) throw new Error('Camera video not available')
      if (video.videoWidth && video.videoHeight) return
      await new Promise((resolve, reject) => {
        const onLoaded = () => {
          video.removeEventListener('loadedmetadata', onLoaded)
          video.removeEventListener('error', onError)
          resolve()
        }
        const onError = (e) => {
          video.removeEventListener('loadedmetadata', onLoaded)
          video.removeEventListener('error', onError)
          reject(e)
        }
        video.addEventListener('loadedmetadata', onLoaded)
        video.addEventListener('error', onError)
      })
    },
    async capturePhoto() {
      if (!this.camera.active) return
      try {
        let blob = null
        if (window.ImageCapture && this.camera.stream) {
          try {
            const track = this.camera.stream.getVideoTracks()[0]
            if (track) {
              const capture = new ImageCapture(track)
              let photoSettings
              if (capture.getPhotoCapabilities) {
                const caps = await capture.getPhotoCapabilities()
                const width = caps.imageWidth && caps.imageWidth.max ? caps.imageWidth.max : undefined
                const height = caps.imageHeight && caps.imageHeight.max ? caps.imageHeight.max : undefined
                if (width || height) {
                  photoSettings = { imageWidth: width, imageHeight: height }
                }
              }
              blob = await capture.takePhoto(photoSettings)
            }
          } catch (e) {
            blob = null
          }
        }
        if (!blob) {
          await this.ensureVideoReady()
          const video = this.$refs.cameraVideo
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          blob = await new Promise((resolve, reject) => {
            canvas.toBlob((b) => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/jpeg', 0.98)
          })
        }
        const mime = (blob && blob.type) ? blob.type : 'image/jpeg'
        const ext = mime === 'image/png' ? 'png' : (mime === 'image/webp' ? 'webp' : 'jpg')
        const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '')
        this.camera.sequence += 1
        const filename = `camera_${stamp}_${String(this.camera.sequence).padStart(3, '0')}.${ext}`
        const file = new File([blob], filename, { type: mime })
        await this.addFilesChunked([{ file, relPath: filename }])
        this.requestUpload()
      } catch (e) {
        this.camera.error = 'Capture failed. Try again.'
      }
    },
    onUniChange() {
      this.selected_ceremony = null
      axios.get(`/universities/${this.selected_university.id}/ceremonies`).then(r => this.ceremonies = r.data)
    },
    formatCeremonyOption(c) {
      const formattedDate = new Date(c.datetime).toLocaleDateString('en-GB', {weekday: 'long', year:'numeric', month:'long', day:'numeric', hour:'numeric', minute:'2-digit', hour12: true})
      return c.label ? `${formattedDate} - ${c.label}` : formattedDate
    },
    async onDrop(e) {
      const dt = e.dataTransfer
      // Prefer traversing directories via DataTransferItem API when available
      let collected = []
      this.collecting = true
      this.collectStats.found = 0
      if (dt && dt.items && dt.items.length && dt.items[0].webkitGetAsEntry) {
        collected = await this.collectFromDataTransferItems(dt.items, () => {
          this.collectStats.found++
        })
      } else {
        const files = Array.from(dt.files || [])
        collected = files.map(f => ({ file: f, relPath: f.webkitRelativePath || f.name }))
        this.collectStats.found = collected.length
      }
      await this.addFilesChunked(collected)
      this.collecting = false
      if (this.files.length) this.ui.dropzoneCollapsed = true
    },
    onFileChange(e) {
      const files = Array.from(e.target.files || [])
      const collected = files.map(f => ({ file: f, relPath: f.webkitRelativePath || f.name }))
      this.addFilesChunked(collected).then(() => { if (this.files.length) this.ui.dropzoneCollapsed = true })
      e.target.value = ''
    },
    async onCameraCapture(e) {
      const files = Array.from(e.target.files || [])
      if (!files.length) return
      const collected = files.map(f => ({ file: f, relPath: f.name }))
      await this.addFilesChunked(collected)
      e.target.value = ''
      if (this.isCameraMode) this.requestUpload()
      if (this.isCameraMode) {
        setTimeout(() => this.scheduleCameraStart(), 300)
      }
    },
    async collectFromDataTransferItems(items, onFileCb) {
      const results = []
      const promises = []
      const toArray = (list) => Array.prototype.slice.call(list)
      const readDir = async (dirEntry) => {
        const reader = dirEntry.createReader()
        const readAll = async () => {
          return new Promise((resolve) => {
            const entries = []
            const read = () => {
              reader.readEntries(async (batch) => {
                if (!batch.length) return resolve(entries)
                entries.push(...batch)
                read()
              })
            }
            read()
          })
        }
        const entries = await readAll()
        for (const entry of entries) {
          if (entry.isDirectory) await readDir(entry)
          else if (entry.isFile) {
            const p = new Promise((res) => {
              entry.file((file) => {
                const item = { file, relPath: entry.fullPath?.replace(/^\//, '') || file.name }
                results.push(item)
                if (onFileCb) onFileCb(item)
                res()
              }, () => res())
            })
            promises.push(p)
          }
        }
      }
      for (const it of toArray(items)) {
        const entry = it.webkitGetAsEntry()
        if (!entry) continue
        if (entry.isDirectory) await readDir(entry)
        else if (entry.isFile) {
          const p = new Promise((res) => {
            entry.file((file) => { const item = { file, relPath: file.name }; results.push(item); if (onFileCb) onFileCb(item); res() }, () => res())
          })
          promises.push(p)
        }
      }
      await Promise.all(promises)
      return results
    },
    async addFilesChunked(items) {
      const chunkSize = 200
      let skipped = 0
      let renamed = 0
      const existingNames = new Set(this.files.map(f => f.finalName.toLowerCase()))
      let nextId = this.files.length
      for (let i = 0; i < items.length; i += chunkSize) {
        const batch = items.slice(i, i + chunkSize)
        const mapped = []
        for (const it of batch) {
          const file = it.file
          const relPath = it.relPath || file.webkitRelativePath || file.name
          const mime = this.detectMime(file)
          if (!this.isSupportedImage(file, mime)) { skipped++; continue }
          const suggested = this.suggestFileName(file.name, relPath)
          const { finalName, wasRenamed } = this.ensureUniqueName(suggested, existingNames)
          if (wasRenamed) renamed++
          mapped.push({ id: nextId++, file, relPath, finalName, mime, status: 'queued', progress: 0, _renamed: wasRenamed, totalBytes: file.size, _lastFullLoaded: 0 })
        }
        if (mapped.length) {
          // Update totals and push in one go to minimize reactivity churn
          this.overall.totalBytes += mapped.reduce((acc, f) => acc + f.file.size, 0)
          this.files = this.files.concat(mapped)
        }
        // Yield to UI thread for responsiveness
        await new Promise(r => requestAnimationFrame(r))
      }
      this.stats.skipped += skipped
      this.stats.renamed += renamed
    },
    clearQueue() {
      this.files = []
      this.stats = { skipped: 0, renamed: 0, uploaded: 0, skippedDuplicates: 0, failed: 0 }
      this.overall = { totalBytes: 0, uploadedBytes: 0 }
      this.uploadSessionId = ''
    },
    requestUpload() {
      this.uploadRequested = true
      if (!this.uploading) this.startUpload()
    },
    async startUpload() {
      if (!this.selected_ceremony || this.files.length === 0) return
      if (this.uploading) return
      if (!this.uploadSessionId) this.uploadSessionId = this.createUploadSessionId()
      this.uploading = true
      this.uploadRequested = false
      trackEvent(this.$route.path, {
        label: 'SNAPSHOT_UPLOAD_SESSION_STARTED',
        ceremony_id: this.selected_ceremony,
        upload_session_id: this.uploadSessionId,
        file_count: this.files.filter(f => f.status === 'queued' || f.status === 'error').length,
      })
      try {
        const queue = this.files.filter(f => f.status === 'queued' || f.status === 'error')
        if (!queue.length) return
        while (queue.length) {
          const batch = queue.splice(0, this.presignBatchSize)
          await this.ensurePresignBatch(batch, { force: true })
          const uploadable = batch.filter(f => f._presign && !f._presign.skipped && f.status !== 'error')
          const workers = Array.from({ length: Math.min(this.concurrency, uploadable.length) }).map(() => this.worker(uploadable))
          await Promise.all(workers)
        }
      } catch (e) {
        console.error('Upload failed to start', e)
      } finally {
        this.uploading = false
        if (this.terminalCount === this.files.length) {
          trackEvent(this.$route.path, {
            label: 'SNAPSHOT_UPLOAD_SESSION_COMPLETED',
            ceremony_id: this.selected_ceremony,
            upload_session_id: this.uploadSessionId,
            uploaded: this.uploadedCount,
            skipped: this.skippedDuplicateCount,
            failed: this.failedCount,
          })
        }
        if (this.uploadRequested) this.startUpload()
      }
    },
    async worker(queue) {
      while (queue.length) {
        const item = queue.shift()
        if (!item) break
        await this.uploadOne(item)
      }
    },
    async uploadOne(item) {
      item.status = 'uploading'
      const totalBytes = item.file.size
      item.totalBytes = totalBytes
      // Reset per-request counters
      item._lastFullLoaded = 0
      const update = () => {
        const done = item._lastFullLoaded
        item.progress = Math.min(100, Math.floor((done / Math.max(1, totalBytes)) * 100))
      }

      const attemptUpload = async (attempt = 1) => {
        // Ensure we have fresh presigned URLs for this file
        const presign = await this.ensurePresign(item)
        if (presign.skipped) {
          item.status = 'skipped duplicate'
          item.progress = 100
          trackEvent(this.$route.path, {
            label: 'SNAPSHOT_UPLOAD_FILE_SKIPPED',
            ceremony_id: this.selected_ceremony,
            upload_session_id: this.uploadSessionId,
            photo_id: presign.photo_id,
            filename: item.finalName,
          })
          return 'skipped'
        }
        const put = (url, blob, contentType) => axios.put(url, blob, {
          headers: { 'Content-Type': contentType },
          transformRequest: (data, headers) => {
            delete headers.Authorization
            delete headers.authorization
            if (headers.common) {
              headers.common = { ...headers.common }
              delete headers.common.Authorization
              delete headers.common.authorization
            }
            return data
          },
          onUploadProgress: (e) => {
            const prev = item._lastFullLoaded || 0
            const delta = Math.max(0, e.loaded - prev)
            item._lastFullLoaded = e.loaded
            this.overall.uploadedBytes += delta
            update()
          }
        })
        try {
          await put(presign.fullres.url, item.file, presign.fullres.content_type || 'application/octet-stream')
          return 'uploaded'
        } catch (err) {
          const status = err?.response?.status
          // For 403 (expired signature) or network timeouts, retry with backoff and fresh presign
          if (attempt < this.maxRetries && (!status || status === 403 || status >= 500)) {
            const delay = Math.min(30000, 1000 * Math.pow(2, attempt))
            await new Promise(r => setTimeout(r, delay))
            // Roll back credited progress for this file attempt before retrying
            this.overall.uploadedBytes -= (item._lastFullLoaded || 0)
            item._lastFullLoaded = 0
            item._presign = null
            return attemptUpload(attempt + 1)
          }
          throw err
        }
      }

      try {
        const ok = await attemptUpload(1)
        if (ok === 'uploaded') {
          item.status = 'processing'
          item.progress = 100
          trackEvent(this.$route.path, {
            label: 'SNAPSHOT_UPLOAD_FILE_DONE',
            ceremony_id: this.selected_ceremony,
            upload_session_id: this.uploadSessionId,
            photo_id: item._presign && item._presign.photo_id,
            filename: item.finalName,
          })
        }
      } catch (e) {
        console.error('Upload error', e)
        item.status = 'error'
        trackEvent(this.$route.path, {
          label: 'SNAPSHOT_UPLOAD_FILE_FAILED',
          ceremony_id: this.selected_ceremony,
          upload_session_id: this.uploadSessionId,
          filename: item.finalName,
        })
      }
    },
    async ensurePresign(item) {
      if (item._presign && item._presign.filename === item.finalName) return item._presign
      await this.ensurePresignBatch([item])
      if (item._presign) return item._presign
      throw new Error('Failed to obtain presign for file')
    },
    async ensurePresignBatch(items, options = {}) {
      const pending = items.filter(item => {
        if (item.status === 'skipped duplicate') return false
        if (options.force) return true
        return !(item._presign && item._presign.filename === item.finalName)
      })
      if (!pending.length) return
      pending.forEach(item => {
        item.status = 'presigning'
      })
      const body = {
        ceremony_id: this.selected_ceremony,
        upload_session_id: this.uploadSessionId,
        session_file_count: this.files.length,
        session_total_bytes: this.overall.totalBytes,
        files: pending.map(item => ({
          filename: item.finalName,
          content_type: item.mime,
          size: item.file.size,
        }))
      }
      let r
      try {
        r = await axios.post(`${process.env.VUE_APP_SNAPSHOT_API_BASE}/upload/presign`, body)
      } catch (e) {
        pending.forEach(item => {
          item.status = 'error'
        })
        throw e
      }
      const byFilename = new Map((r.data.items || []).map(item => [item.filename, item]))
      pending.forEach(item => {
        const presign = byFilename.get(item.finalName)
        if (!presign) {
          item.status = 'error'
          return
        }
        item._presign = presign
        if (presign.skipped) {
          item.status = 'skipped duplicate'
          item.progress = 100
          trackEvent(this.$route.path, {
            label: 'SNAPSHOT_UPLOAD_FILE_SKIPPED',
            ceremony_id: this.selected_ceremony,
            upload_session_id: this.uploadSessionId,
            photo_id: presign.photo_id,
            filename: item.finalName,
          })
        } else {
          item.status = 'queued'
        }
      })
    },
    createUploadSessionId() {
      if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
      return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`
    },
    formatBytes(bytes) {
      if (!bytes || bytes <= 0) return '0 B'
      const units = ['B','KB','MB','GB','TB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const val = bytes / Math.pow(1024, i)
      return `${val.toFixed(val < 10 ? 1 : 0)} ${units[i]}`
    },
    detectMime(file) {
      if (file && file.type) return file.type
      const name = (file && file.name) || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
      if (ext === 'png') return 'image/png'
      if (ext === 'webp') return 'image/webp'
      return 'application/octet-stream'
    },
    isSupportedImage(file, mime) {
      const okType = mime && (mime.startsWith('image/'))
      const name = (file && file.name) || ''
      const ext = name.substring(name.lastIndexOf('.') + 1).toLowerCase()
      const okExt = ['jpg','jpeg','png','webp'].includes(ext)
      return okType && okExt
    },
    sanitizeBaseName(name) {
      // Keep only allowed chars (server enforces similar pattern); preserve extension
      const dot = name.lastIndexOf('.')
      const base = dot > 0 ? name.slice(0, dot) : name
      const ext = dot > 0 ? name.slice(dot) : ''
      const cleanedBase = base.replace(/[^A-Za-z0-9_.\- ]+/g, ' ').replace(/\s+/g, ' ').trim()
      const cleanedExt = ext.replace(/[^A-Za-z0-9.]/g, '')
      let sanitized = cleanedBase || 'image'
      // Enforce max length 200 inc. extension
      const maxLen = 200 - cleanedExt.length
      if (sanitized.length > maxLen) sanitized = sanitized.slice(0, Math.max(0, maxLen))
      return sanitized + cleanedExt
    },
    suggestFileName(originalName, relPath) {
      // Use folder components as a prefix (flattened) to reduce collisions, but no slashes
      const dot = originalName.lastIndexOf('.')
      const ext = dot >= 0 ? originalName.slice(dot) : ''
      const base = dot >= 0 ? originalName.slice(0, dot) : originalName
      const pathOnly = (relPath || '').split('/').slice(0, -1).filter(Boolean)
      // Only include up to last two folders to avoid overly long names
      const prefixParts = pathOnly.slice(-2).map(p => p.replace(/[^A-Za-z0-9_.\- ]+/g, ' ').replace(/\s+/g, ' ').trim()).filter(Boolean)
      const prefixed = (prefixParts.length ? (prefixParts.join('-') + '-') : '') + base
      return this.sanitizeBaseName(prefixed + ext)
    },
    ensureUniqueName(name, existingLowerSet) {
      const dot = name.lastIndexOf('.')
      const ext = dot >= 0 ? name.slice(dot) : ''
      const base = dot >= 0 ? name.slice(0, dot) : name
      let candidate = name
      let suffix = 1
      let wasRenamed = false
      while (existingLowerSet.has(candidate.toLowerCase())) {
        wasRenamed = true
        suffix += 1
        const next = `${base} (${suffix})${ext}`
        candidate = this.sanitizeBaseName(next)
      }
      existingLowerSet.add(candidate.toLowerCase())
      return { finalName: candidate, wasRenamed }
    },
  }
}
</script>

<style scoped>
.camera-stage {
  position: fixed;
  inset: 0;
  background: #0b0b0b;
  color: #fff;
  z-index: 50;
  display: flex;
  flex-direction: column;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  gap: 16px;
}

.camera-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.camera-title {
  font-size: 18px;
  font-weight: 600;
}

.camera-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
}

.camera-text-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
}

.camera-preview {
  position: relative;
  flex: 1;
  border-radius: 18px;
  overflow: hidden;
  background: #000;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  color: rgba(255, 255, 255, 0.75);
}

.camera-placeholder-title {
  font-size: 16px;
  font-weight: 600;
}

.camera-placeholder-text {
  font-size: 12px;
}

.camera-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.camera-controls-left,
.camera-controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.camera-button {
  border-radius: 999px;
  padding: 10px 18px;
  font-size: 14px;
  border: 1px solid transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.camera-button-primary {
  background: #ffffff;
  color: #111;
}

.camera-button-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.2);
}

.camera-button[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
}

.camera-select-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.camera-select {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
}

.camera-error {
  font-size: 12px;
  color: #ffb3b3;
}
</style>
